import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { Row, Col, Alert } from 'react-bootstrap';
import { isAuthenticated } from '../services/KeycloakService';
import { encodeBase64Url, sortSubmodelDescriptorsByOrderReference } from './Utils';
import { fetchSubmodelDescriptors } from '../services/DppManagerService';
import Config from '../config/Config';
import DppOverview from './DppOverview';
import DppActionList from './DppActionList';
import DppSubmodelTabs from './DppSubmodelTabs';
import WaterMeterReadingsModal from './modals/WaterMeterReadingsModal';
import DownloadModal from './modals/DownloadModal';
import ReturnNumberModal from './modals/ReturnNumberModal';
import WaterMeterReadingsHandler from './handlers/WaterMeterReadingsHandler';
import DownloadHandler from './handlers/DownloadHandler';
import CO2ConsumptionModal from './modals/CO2ConsumptionModal';
import ReportProblemModal from './modals/ReportProblemModal';
import CO2ConsumptionHandler from './handlers/CO2ConsumptionHandler';
import ReportProblemHandler from './handlers/ReportProblemHandler';
import ReturnNumberHandler from './handlers/ReturnNumberHandler';

function DppView({ shell, query }) {
  const navigate = useNavigate();

  const intl = useIntl();
  const locale = intl.locale;

  const assetAdministrationShell = shell ?? {};
  const aasId = assetAdministrationShell.id;
  const aasIdBase64 = encodeBase64Url(aasId);

  const [submodelDescriptors, setSubmodelDescriptors] = useState([]);
  const [submodelIdShortMap, setSubmodelIdShortMap] = useState(new Map()); // New state for ID map
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [downloadModalShow, setDownloadModalShow] = useState(false);
  const [returnNumberModalShow, setReturnNumberModalShow] = useState(false);
  const [co2ConsumptionModalShow, setCO2ConsumptionModalShow] = useState(false);
  const [waterMeterReadingsModalShow, setWaterMeterReadingsModalShow] = useState(false);
  const [reportProblemModalShow, setReportProblemModalShow] = useState(false);

  const submodelOrderReference = Config.dpp.submodels.orderByIdShort;

  useEffect(() => {
    const fetchSubmodelData = async () => {
      if (!aasIdBase64 || !isAuthenticated()) {
        setError('Not authenticated or no AAS ID provided');
        return;
      }

      setError(null);

      try {
        const smDescriptors = await fetchSubmodelDescriptors(aasId);
        const sortedSmDescriptors = sortSubmodelDescriptorsByOrderReference(smDescriptors, submodelOrderReference);
        
        // Create a map of idShort to id
        const idMap = new Map(sortedSmDescriptors.map(descriptor => [descriptor.idShort, descriptor.id]));
        
        setSubmodelDescriptors(sortedSmDescriptors);
        setSubmodelIdShortMap(idMap);

      } catch (err) {
        console.error('Failed to load sub-model references', err);
        setError(err.message);
      }
    };

    fetchSubmodelData();
  }, [aasIdBase64]);

  // Get smId for TechnicalData from the map
  const smIdTechnicalData = submodelIdShortMap.get('TechnicalData');
  const smIdCarbonFootprint = submodelIdShortMap.get('CarbonFootprint');
  const smIdServiceRequestNotification = submodelIdShortMap.get('ServiceRequestNotification');
  const smIdCircularEconomy = submodelIdShortMap.get('CircularEconomy');

  if (!assetAdministrationShell) {
    return <div>No Asset Administration Shell available</div>;
  }

  // Action button click handlers
  const naviageAccessControl = () => navigate('/access-control?query=' + query);
  const showDownloadModal = () => setDownloadModalShow(true);
  const showReturnNumberModal = () => setReturnNumberModalShow(true);
  const showCO2ConsumptionModal = () => setCO2ConsumptionModalShow(true);
  const showWaterMeterReadingsModal = () => setWaterMeterReadingsModalShow(true);
  const showReportProblemModal = () => setReportProblemModalShow(true);

  return (
    <>
      {error && (
        <Alert
          variant="danger"
          dismissible
          onClose={() => setError('')}
          style={{ borderRadius: '20px', marginBottom: '25px' }}
        >
          {error}
        </Alert>
      )}

      {success && (
        <Alert
          variant="success"
          dismissible
          onClose={() => setSuccess('')}
          style={{ borderRadius: '20px', marginBottom: '25px' }}
        >
          {success}
        </Alert>
      )}

      <Row className="gx-4 gy-5 mb-4">
        <Col lg={12} xl={9}>
          {assetAdministrationShell.id ? (
            <DppOverview
              assetAdministrationShell={assetAdministrationShell}
              submodelDescriptors={submodelDescriptors}
            />
          ) : (
            <div>No valid Asset Administration Shell data</div>
          )}
        </Col>

        <Col lg={12} xl={3}>
          <DppActionList
            onChangeAccessControl={naviageAccessControl}
            onDownload={showDownloadModal}
            onSetReturnNumber={showReturnNumberModal}
            onAddCO2Consumption={showCO2ConsumptionModal}
            onAddWaterMeterReadings={showWaterMeterReadingsModal}
            onReportProblem={showReportProblemModal}
          />
        </Col>
      </Row>

      <DppSubmodelTabs submodelDescriptors={submodelDescriptors} />

      <DownloadModal
        show={downloadModalShow}
        onClose={() => setDownloadModalShow(false)}
        onDownload={(data) =>
          DownloadHandler({ data, aasId, aasIdBase64, locale, setError, setSuccess })
        }
      />

      <ReturnNumberModal
        show={returnNumberModalShow}
        onClose={() => setReturnNumberModalShow(false)}
        onSave={(data) => ReturnNumberHandler({ data, setError, setSuccess, smId: smIdCircularEconomy })}
      />

      <CO2ConsumptionModal
        show={co2ConsumptionModalShow}
        onClose={() => setCO2ConsumptionModalShow(false)}
        onSave={(data) => CO2ConsumptionHandler({ data, setError, setSuccess, smId: smIdCarbonFootprint })}
      />

      <WaterMeterReadingsModal
        show={waterMeterReadingsModalShow}
        onClose={() => setWaterMeterReadingsModalShow(false)}
        onSave={(data) =>
          WaterMeterReadingsHandler({ data, setError, setSuccess, smId: smIdTechnicalData })
        }
      />

      <ReportProblemModal
        show={reportProblemModalShow}
        onClose={() => setReportProblemModalShow(false)}
        onSave={(data) => ReportProblemHandler({ data, setError, setSuccess, smId: smIdServiceRequestNotification, assetInformation: assetAdministrationShell.assetInformation})}
      />
    </>
  );
}

export default DppView;